import React, { useEffect, useState } from 'react';
import onboardingService from '../services/OnboardingService';
import {
  accService,
  ACCConnection,
  ACCFileVersion,
  ACCFolderItem,
  ACCHub,
  ACCProject
} from '../services/ACCService';
import { apiService } from '../services/ApiService';
import { fileService, FileUploadProgress } from '../services/FileService';
import { coordinateTransformService } from '../services/CoordinateTransformService';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { useDebounce } from '../hooks/useDebounce';
import { OnboardingSession, OnboardingTransform, OnboardingUserSearchResult } from '../types/Onboarding';

interface ProjectOnboardingPageProps {
  isAdmin: boolean;
  onOpenSiteViewer?: (siteId: string) => void;
  embeddedSiteId?: string | null;
  onProjectCreated?: (session: OnboardingSession) => void;
  onSessionChange?: (session: OnboardingSession) => void;
}

const createDefaultTransform = (): OnboardingTransform => ({
  translation: { x: 0, y: 0, z: 0 },
  rotationDeg: 0,
  scale: { x: 1, y: 1, z: 1 },
  swapXY: false,
  flipX: false,
  flipY: false,
  flipZ: false
});

const getTransformFromSession = (session: OnboardingSession | null): OnboardingTransform => {
  const transform = session?.site?.customData?.coordinate_transforms?.panorama_to_rvt_model
    || session?.site?.customData?.panorama_to_rvt_model;

  if (!transform) return createDefaultTransform();

  return {
    translation: {
      x: Number(transform.translation?.x ?? 0),
      y: Number(transform.translation?.y ?? 0),
      z: Number(transform.translation?.z ?? 0)
    },
    rotationDeg: Number(transform.rotationDeg ?? 0),
    scale: {
      x: Number(transform.scale?.x ?? 1),
      y: Number(transform.scale?.y ?? 1),
      z: Number(transform.scale?.z ?? 1)
    },
    swapXY: Boolean(transform.swapXY),
    flipX: Boolean(transform.flipX),
    flipY: Boolean(transform.flipY),
    flipZ: Boolean(transform.flipZ)
  };
};

const getSessionStep = (session: OnboardingSession | null): number => {
  if (!session) return 1;
  if (!session.modelSourceType) return 2;
  if (session.modelSourceType === 'acc' && !session.accLinkId) return 3;
  if ((session.modelSourceType === 'local_ifc' || session.modelSourceType === 'local_rvt') && !session.localModelFileId) return 3;
  if (session.e57FileId && !session.alignmentVerifiedAt) return 5;
  if (session.invites.length === 0) return 6;
  if (!session.readiness.canPublish) return 7;
  return session.publishedAt ? 8 : 7;
};

// DT design-language chip hues per status key (matches the --dt-* semantic tokens).
const CHIP_TOKENS: Record<string, string> = {
  draft: 'warn',
  processing: 'prog',
  ready: 'good',
  live: 'good',
  failed: 'bad',
  queued: 'info',
  sent: 'info',
  accepted: 'good',
  expired: 'neutral',
  error: 'bad',
  failed_email: 'bad'
};

const chipStyle = (statusKey: string): React.CSSProperties => {
  const token = CHIP_TOKENS[statusKey];
  if (!token || token === 'neutral') {
    return { color: 'var(--dt-text-2)', background: 'var(--dt-surface-3)' };
  }
  return { color: `var(--dt-${token})`, background: `var(--dt-${token}-t)` };
};

const renderChip = (label: string, statusKey: string, withDot = false) => {
  const style = chipStyle(statusKey);
  return (
    <span className="dt-chip" style={style}>
      {withDot && <span className="cd" style={{ background: style.color }} />}
      {label}
    </span>
  );
};

const initials = (value: string): string => {
  const source = (value || '').trim();
  if (!source) return '?';
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const IC_ONBOARD = (
  <svg viewBox="0 0 24 24">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const IC_CHECK = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const avatarStyle: React.CSSProperties = { width: 30, height: 30, fontSize: '.72rem' };

const ProjectOnboardingPage: React.FC<ProjectOnboardingPageProps> = ({
  isAdmin,
  onOpenSiteViewer,
  embeddedSiteId,
  onProjectCreated,
  onSessionChange
}) => {
  const { t, isRTL } = useTranslation();
  const { refreshToken } = useAuth();
  const isEmbedded = embeddedSiteId !== undefined;

  const [sessions, setSessions] = useState<OnboardingSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<OnboardingSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteDescription, setNewSiteDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const [siteNameDraft, setSiteNameDraft] = useState('');
  const [siteDescriptionDraft, setSiteDescriptionDraft] = useState('');
  const [savingSiteDetails, setSavingSiteDetails] = useState(false);

  const [modelUploadFile, setModelUploadFile] = useState<File | null>(null);
  const [e57UploadFile, setE57UploadFile] = useState<File | null>(null);
  const [uploadingModel, setUploadingModel] = useState(false);
  const [uploadingE57, setUploadingE57] = useState(false);
  const [e57UploadProgress, setE57UploadProgress] = useState<FileUploadProgress | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [savingTransform, setSavingTransform] = useState(false);
  const [verifyingAlignment, setVerifyingAlignment] = useState(false);

  const [transform, setTransform] = useState<OnboardingTransform>(createDefaultTransform());
  const alignmentDiagnostics = coordinateTransformService.getCoordinateAlignmentDiagnostics({
    assetCustomData: selectedSession?.site?.customData || {}
  });
  const formatCoordinateValue = (value: any): string => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(3) : 'n/a';
  };
  const formatCoordinatePoint = (point: any): string => {
    if (!point) return 'n/a';
    return `${formatCoordinateValue(point.x)}, ${formatCoordinateValue(point.y)}, ${formatCoordinateValue(point.z)}`;
  };
  const formatCoordinateBounds = (bounds: any): string => {
    if (!bounds?.min || !bounds?.max) return 'n/a';
    return `${formatCoordinatePoint(bounds.min)} → ${formatCoordinatePoint(bounds.max)}`;
  };

  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<OnboardingUserSearchResult[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [newInviteName, setNewInviteName] = useState('');
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteOrganization, setNewInviteOrganization] = useState('');
  const [addingInvite, setAddingInvite] = useState(false);

  const [managerSearchQuery, setManagerSearchQuery] = useState('');
  const [managerSearchResults, setManagerSearchResults] = useState<OnboardingUserSearchResult[]>([]);
  const [assigningManager, setAssigningManager] = useState(false);

  const [accConnections, setAccConnections] = useState<ACCConnection[]>([]);
  const [accHubs, setAccHubs] = useState<ACCHub[]>([]);
  const [accProjects, setAccProjects] = useState<ACCProject[]>([]);
  const [accFolderContents, setAccFolderContents] = useState<ACCFolderItem[]>([]);
  const [accFolderPath, setAccFolderPath] = useState<Array<{ id: string; name: string }>>([]);
  const [accSelectedConnection, setAccSelectedConnection] = useState<ACCConnection | null>(null);
  const [accSelectedHub, setAccSelectedHub] = useState<ACCHub | null>(null);
  const [accSelectedProject, setAccSelectedProject] = useState<ACCProject | null>(null);
  const [accPendingFile, setAccPendingFile] = useState<ACCFolderItem | null>(null);
  const [accVersions, setAccVersions] = useState<ACCFileVersion[]>([]);
  const [accSelectedVersion, setAccSelectedVersion] = useState<number | null>(null);
  const [accSelectedVersionUrn, setAccSelectedVersionUrn] = useState<string | null>(null);
  const [accLoading, setAccLoading] = useState(false);
  const [linkingACC, setLinkingACC] = useState(false);

  const loadProjects = async (preserveSelection = true) => {
    try {
      setError(null);
      setRefreshing(true);
      const nextSessions = await onboardingService.listProjects();
      setSessions(nextSessions);

      if (isEmbedded) {
        const embeddedSession = embeddedSiteId
          ? nextSessions.find((session) => session.siteId === embeddedSiteId) || null
          : null;
        setSelectedSessionId(embeddedSession?.id || null);
        setSelectedSession(embeddedSession);
        return;
      }

      if (nextSessions.length === 0) {
        setSelectedSessionId(null);
        setSelectedSession(null);
        return;
      }

      const nextSelectedId = preserveSelection && selectedSessionId && nextSessions.some((session) => session.id === selectedSessionId)
        ? selectedSessionId
        : nextSessions[0].id;
      setSelectedSessionId(nextSelectedId);
      setSelectedSession(nextSessions.find((session) => session.id === nextSelectedId) || nextSessions[0]);
    } catch (err: any) {
      setError(err.message || 'Failed to load onboarding projects');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const refreshSelectedSession = async (sessionId = selectedSessionId) => {
    if (!sessionId) return;
    try {
      const nextSession = await onboardingService.getProject(sessionId);
      setSelectedSession(nextSession);
      setSessions((prev) => prev.map((session) => session.id === sessionId ? nextSession : session));
    } catch (err: any) {
      setError(err.message || 'Failed to refresh onboarding project');
    }
  };

  useEffect(() => {
    loadProjects(false);
    // Load once per embedded site. loadProjects is re-created every render and sets the
    // sessions it loads, so depending on it would re-fetch the list on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedSiteId]);

  useEffect(() => {
    if (!selectedSessionId) return;
    refreshSelectedSession(selectedSessionId);
    // Fetch once per selected session — refreshSelectedSession is re-created every render
    // and writes selectedSession, so depending on it would loop on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId]);

  useEffect(() => {
    if (!isEmbedded) return;

    if (!embeddedSiteId) {
      setSelectedSessionId(null);
      setSelectedSession(null);
      return;
    }

    const embeddedSession = sessions.find((session) => session.siteId === embeddedSiteId) || null;
    if (!embeddedSession) {
      setSelectedSessionId(null);
      setSelectedSession(null);
      return;
    }

    if (selectedSessionId !== embeddedSession.id) {
      setSelectedSessionId(embeddedSession.id);
    }
    setSelectedSession(embeddedSession);
  }, [embeddedSiteId, isEmbedded, selectedSessionId, sessions]);

  useEffect(() => {
    setSiteNameDraft(selectedSession?.site.name || '');
    setSiteDescriptionDraft(selectedSession?.site.description || '');
    setTransform(getTransformFromSession(selectedSession));
    // Seeds the edit drafts. Keyed on the SOURCE FIELDS, not the session object: that object
    // is replaced wholesale by the 5s ACC sync poll below, which would re-seed the inputs
    // out from under whoever is typing in them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.id, selectedSession?.site.name, selectedSession?.site.description, selectedSession?.site.customData]);

  useEffect(() => {
    if (!selectedSession?.accLink) return;
    if (!['pending', 'syncing'].includes(selectedSession.accLink.sync_status)) return;

    const timer = window.setInterval(() => {
      refreshSelectedSession(selectedSession.id);
    }, 5000);

    return () => window.clearInterval(timer);
    // The poll must survive its own results: refreshSelectedSession is re-created every
    // render and replaces selectedSession (hence accLink), so either dep would tear the
    // interval down and restart it on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.id, selectedSession?.accLink?.sync_status]);

  useEffect(() => {
    if (!selectedSession || selectedSession.modelSourceType !== 'acc') return;

    let cancelled = false;
    const loadConnections = async () => {
      try {
        setAccLoading(true);
        const connections = await accService.getConnections(selectedSession.site.id);
        if (cancelled) return;
        setAccConnections(connections);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to load ACC connections');
        }
      } finally {
        if (!cancelled) {
          setAccLoading(false);
        }
      }
    };

    loadConnections();
    return () => {
      cancelled = true;
    };
    // Keyed on the three fields it reads. The session object itself is replaced on every
    // 5s sync poll, which would re-fetch the ACC connection list just as often.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.id, selectedSession?.modelSourceType, selectedSession?.site.id]);

  const runUserSearch = useDebounce(async (sessionId: string, query: string) => {
    try {
      setSearchingUsers(true);
      const results = await onboardingService.searchUsers(sessionId, query);
      setUserSearchResults(results);
    } catch (err: any) {
      setError(err.message || 'Failed to search users');
    } finally {
      setSearchingUsers(false);
    }
  }, 250);

  useEffect(() => {
    // Read the id, not the session object: the object churns on every sync poll, and a
    // re-run would fire another /search. runUserSearch is a useDebounce result — stable.
    const sessionId = selectedSession?.id;
    if (!sessionId || userSearchQuery.trim().length < 2) {
      setUserSearchResults([]);
      runUserSearch.cancel();
      return;
    }

    runUserSearch(sessionId, userSearchQuery.trim());
  }, [selectedSession?.id, userSearchQuery, runUserSearch]);

  const runManagerSearch = useDebounce(async (sessionId: string, query: string) => {
    try {
      const results = await onboardingService.searchUsers(sessionId, query);
      setManagerSearchResults(results);
    } catch (err: any) {
      setError(err.message || 'Failed to search onboarding managers');
    }
  }, 250);

  useEffect(() => {
    // Same as the user search above: key on the id, not the churning session object.
    const sessionId = selectedSession?.id;
    if (!isAdmin || !sessionId || managerSearchQuery.trim().length < 2) {
      setManagerSearchResults([]);
      runManagerSearch.cancel();
      return;
    }

    runManagerSearch(sessionId, managerSearchQuery.trim());
  }, [isAdmin, selectedSession?.id, managerSearchQuery, runManagerSearch]);

  useEffect(() => {
    if (selectedSession && onSessionChange) {
      onSessionChange(selectedSession);
    }
  }, [onSessionChange, selectedSession]);

  const setFlash = (message: string) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(null), 3000);
  };

  const handleCreateProject = async () => {
    if (!newSiteName.trim()) {
      setError('Site name is required');
      return;
    }

    try {
      setCreating(true);
      const session = await onboardingService.createProject({
        siteName: newSiteName.trim(),
        siteDescription: newSiteDescription.trim()
      });
      setSessions((prev) => [session, ...prev]);
      setSelectedSessionId(session.id);
      setSelectedSession(session);
      setNewSiteName('');
      setNewSiteDescription('');
      setShowCreateForm(false);
      setFlash('Onboarding project created');
      onProjectCreated?.(session);
    } catch (err: any) {
      setError(err.message || 'Failed to create onboarding project');
    } finally {
      setCreating(false);
    }
  };

  const handleStartOnboardingForExistingSite = async () => {
    if (!embeddedSiteId) {
      return;
    }

    try {
      setCreating(true);
      const session = await onboardingService.createProjectForSite(embeddedSiteId);
      setSessions((prev) => {
        const existingIndex = prev.findIndex((item) => item.id === session.id);
        if (existingIndex === -1) {
          return [session, ...prev];
        }
        return prev.map((item) => item.id === session.id ? session : item);
      });
      setSelectedSessionId(session.id);
      setSelectedSession(session);
      setFlash('Onboarding started for this site');
      onProjectCreated?.(session);
    } catch (err: any) {
      setError(err.message || 'Failed to start onboarding for this site');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveSiteDetails = async () => {
    if (!selectedSession) return;
    try {
      setSavingSiteDetails(true);
      const updated = await onboardingService.updateProject(selectedSession.id, {
        siteName: siteNameDraft.trim(),
        siteDescription: siteDescriptionDraft.trim(),
        currentStep: 1
      });
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setFlash('Site details saved');
    } catch (err: any) {
      setError(err.message || 'Failed to save site details');
    } finally {
      setSavingSiteDetails(false);
    }
  };

  const handleSetModelSource = async (modelSourceType: 'acc' | 'local_ifc' | 'local_rvt') => {
    if (!selectedSession) return;
    try {
      const updated = await onboardingService.updateProject(selectedSession.id, {
        modelSourceType,
        currentStep: 2
      });
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
    } catch (err: any) {
      setError(err.message || 'Failed to update model source');
    }
  };

  const handleUploadModel = async () => {
    if (!selectedSession || !modelUploadFile) return;

    const extension = modelUploadFile.name.toLowerCase().endsWith('.rvt') ? 'local_rvt' : 'local_ifc';

    try {
      setUploadingModel(true);
      await refreshToken();
      const fileType = modelUploadFile.name.toLowerCase().endsWith('.rvt') ? 'rvt' : 'ifc';
      const uploadResult = await fileService.uploadFile(selectedSession.site.id, modelUploadFile, fileType);
      const updated = await onboardingService.updateProject(selectedSession.id, {
        modelSourceType: extension,
        localModelFileId: uploadResult.file.id,
        currentStep: 3
      });
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setModelUploadFile(null);
      setFlash('Model uploaded');
    } catch (err: any) {
      setError(err.message || 'Failed to upload model');
    } finally {
      setUploadingModel(false);
    }
  };

  const handleUploadE57 = async () => {
    if (!selectedSession || !e57UploadFile) return;

    try {
      setUploadingE57(true);
      setE57UploadProgress(null);
      // Refresh token before upload to ensure it's valid for the entire transfer
      // (browser throttles setTimeout in background tabs, so auto-refresh may not have fired)
      await refreshToken();
      const uploadResult = await fileService.uploadFile(
        selectedSession.site.id,
        e57UploadFile,
        'e57',
        (progress) => setE57UploadProgress(progress)
      );
      const updated = await onboardingService.updateProject(selectedSession.id, {
        e57FileId: uploadResult.file.id,
        currentStep: 4
      });
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setE57UploadFile(null);
      setE57UploadProgress(null);
      setFlash('E57 uploaded');
    } catch (err: any) {
      setE57UploadProgress(null);
      setError(err.message || 'Failed to upload E57');
    } finally {
      setUploadingE57(false);
    }
  };

  const handleSaveAlignment = async () => {
    if (!selectedSession) return;
    try {
      setSavingTransform(true);
      const updated = await onboardingService.saveAlignment(selectedSession.id, transform);
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setFlash('Alignment transform saved');
    } catch (err: any) {
      setError(err.message || 'Failed to save alignment');
    } finally {
      setSavingTransform(false);
    }
  };

  const handleVerifyAlignment = async () => {
    if (!selectedSession) return;
    try {
      setVerifyingAlignment(true);
      const updated = await onboardingService.verifyAlignment(selectedSession.id);
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setFlash('Alignment verified');
    } catch (err: any) {
      setError(err.message || 'Failed to verify alignment');
    } finally {
      setVerifyingAlignment(false);
    }
  };

  const handleAddExistingUser = async (user: OnboardingUserSearchResult) => {
    if (!selectedSession) return;
    try {
      setAddingInvite(true);
      const updated = await onboardingService.addInvite(selectedSession.id, {
        existingUserId: user.id
      });
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setUserSearchQuery('');
      setUserSearchResults([]);
      setFlash(`Added ${user.email}`);
    } catch (err: any) {
      setError(err.message || 'Failed to add user');
    } finally {
      setAddingInvite(false);
    }
  };

  const handleAddNewInvite = async () => {
    if (!selectedSession) return;
    if (!newInviteEmail.trim()) {
      setError('Email is required');
      return;
    }

    try {
      setAddingInvite(true);
      const updated = await onboardingService.addInvite(selectedSession.id, {
        email: newInviteEmail.trim(),
        name: newInviteName.trim(),
        organization: newInviteOrganization.trim()
      });
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setNewInviteEmail('');
      setNewInviteName('');
      setNewInviteOrganization('');
      setFlash('Invitation recipient added');
    } catch (err: any) {
      setError(err.message || 'Failed to add invitation');
    } finally {
      setAddingInvite(false);
    }
  };

  const handleDeleteInvite = async (inviteId: string) => {
    if (!selectedSession) return;
    try {
      const updated = await onboardingService.deleteInvite(selectedSession.id, inviteId);
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
    } catch (err: any) {
      setError(err.message || 'Failed to delete invite');
    }
  };

  const handleAssignManager = async (userId: string) => {
    if (!selectedSession) return;
    try {
      setAssigningManager(true);
      const updated = await onboardingService.assignManager(selectedSession.id, userId);
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setManagerSearchQuery('');
      setManagerSearchResults([]);
      setFlash('Onboarding manager assigned');
    } catch (err: any) {
      setError(err.message || 'Failed to assign onboarding manager');
    } finally {
      setAssigningManager(false);
    }
  };

  const handleRemoveManager = async (userId: string) => {
    if (!selectedSession) return;
    try {
      const updated = await onboardingService.removeManager(selectedSession.id, userId);
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setFlash('Onboarding manager removed');
    } catch (err: any) {
      setError(err.message || 'Failed to remove onboarding manager');
    }
  };

  const handlePublish = async () => {
    if (!selectedSession) return;
    try {
      setPublishing(true);
      const updated = await onboardingService.publishProject(selectedSession.id);
      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setFlash('Project published');
    } catch (err: any) {
      setError(err.message || 'Failed to publish onboarding project');
    } finally {
      setPublishing(false);
    }
  };

  const handleResendEmail = async (emailId: number) => {
    if (!selectedSession) return;
    try {
      await onboardingService.resendEmail(selectedSession.id, emailId);
      await refreshSelectedSession(selectedSession.id);
      setFlash('Email resend triggered');
    } catch (err: any) {
      setError(err.message || 'Failed to resend email');
    }
  };

  const handleConnectACC = async () => {
    if (!selectedSession) return;
    try {
      setAccLoading(true);
      const { authUrl } = await accService.initiateAuth(selectedSession.site.id, '/', 'full');
      window.location.href = authUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to start ACC connection');
    } finally {
      setAccLoading(false);
    }
  };

  const handleSelectConnection = async (connectionId: string) => {
    const connection = accConnections.find((item) => item.id === connectionId) || null;
    setAccSelectedConnection(connection);
    setAccSelectedHub(null);
    setAccSelectedProject(null);
    setAccHubs([]);
    setAccProjects([]);
    setAccFolderContents([]);
    setAccFolderPath([]);
    if (!connection) return;

    try {
      setAccLoading(true);
      const hubs = await accService.getHubs(connection.id);
      setAccHubs(hubs);
    } catch (err: any) {
      setError(err.message || 'Failed to load ACC hubs');
    } finally {
      setAccLoading(false);
    }
  };

  const handleSelectHub = async (hubId: string) => {
    const hub = accHubs.find((item) => item.id === hubId) || null;
    setAccSelectedHub(hub);
    setAccSelectedProject(null);
    setAccProjects([]);
    setAccFolderContents([]);
    setAccFolderPath([]);
    if (!hub || !accSelectedConnection) return;

    try {
      setAccLoading(true);
      const projects = await accService.getProjects(accSelectedConnection.id, hub.id);
      setAccProjects(projects);
    } catch (err: any) {
      setError(err.message || 'Failed to load ACC projects');
    } finally {
      setAccLoading(false);
    }
  };

  const handleSelectProject = async (projectId: string) => {
    const project = accProjects.find((item) => item.id === projectId) || null;
    setAccSelectedProject(project);
    setAccFolderContents([]);
    setAccFolderPath([]);
    if (!project || !accSelectedConnection) return;

    try {
      setAccLoading(true);
      const contents = await accService.getFolderContents(accSelectedConnection.id, project.id, undefined, accSelectedHub?.id);
      setAccFolderContents(contents);
    } catch (err: any) {
      setError(err.message || 'Failed to load ACC folder contents');
    } finally {
      setAccLoading(false);
    }
  };

  const handleNavigateFolder = async (folder: ACCFolderItem) => {
    if (!accSelectedConnection || !accSelectedProject || folder.type !== 'folder') return;

    try {
      setAccLoading(true);
      const nextPath = [...accFolderPath, { id: folder.id, name: folder.name }];
      setAccFolderPath(nextPath);
      const contents = await accService.getFolderContents(accSelectedConnection.id, accSelectedProject.id, folder.id);
      setAccFolderContents(contents);
    } catch (err: any) {
      setError(err.message || 'Failed to load ACC folder');
    } finally {
      setAccLoading(false);
    }
  };

  const handleNavigateUp = async () => {
    if (!accSelectedConnection || !accSelectedProject || accFolderPath.length === 0) return;
    const nextPath = accFolderPath.slice(0, -1);
    setAccFolderPath(nextPath);

    try {
      setAccLoading(true);
      const folderId = nextPath.length > 0 ? nextPath[nextPath.length - 1].id : undefined;
      const contents = await accService.getFolderContents(
        accSelectedConnection.id,
        accSelectedProject.id,
        folderId,
        folderId ? undefined : accSelectedHub?.id
      );
      setAccFolderContents(contents);
    } catch (err: any) {
      setError(err.message || 'Failed to navigate ACC folders');
    } finally {
      setAccLoading(false);
    }
  };

  const handleSelectAccFile = async (file: ACCFolderItem) => {
    if (!accSelectedConnection || !accSelectedProject) return;
    try {
      setAccLoading(true);
      setAccPendingFile(file);
      const versions = await accService.getFileVersions(accSelectedConnection.id, accSelectedProject.id, file.id);
      setAccVersions(versions);
      setAccSelectedVersion(null);
      setAccSelectedVersionUrn(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load ACC file versions');
    } finally {
      setAccLoading(false);
    }
  };

  const handleLinkAccFile = async () => {
    if (!selectedSession || !accSelectedConnection || !accSelectedProject || !accPendingFile) return;

    try {
      setLinkingACC(true);
      const result = await accService.linkFile(
        selectedSession.site.id,
        accSelectedConnection.id,
        accSelectedProject.id,
        accSelectedProject.name,
        accFolderPath.length > 0 ? accFolderPath[accFolderPath.length - 1].id : null,
        accPendingFile.id,
        accPendingFile.name,
        'svf2',
        accSelectedVersion,
        accSelectedVersionUrn,
        true
      );

      const updated = await onboardingService.updateProject(selectedSession.id, {
        modelSourceType: 'acc',
        accLinkId: result.linkId,
        currentStep: 3
      });

      setSelectedSession(updated);
      setSessions((prev) => prev.map((session) => session.id === updated.id ? updated : session));
      setAccPendingFile(null);
      setAccVersions([]);
      setFlash('ACC model linked. Sync is running in the background.');
    } catch (err: any) {
      setError(err.message || 'Failed to link ACC model');
    } finally {
      setLinkingACC(false);
    }
  };

  const currentStep = selectedSession ? getSessionStep(selectedSession) : 0;
  const stepDone = (n: number) => n < currentStep;
  const stepCircle = (n: number) => (
    stepDone(n)
      ? <span className="dt-stepn done">{IC_CHECK}</span>
      : <span className="dt-stepn">{n}</span>
  );
  const diagCell = (label: string, value: React.ReactNode) => (
    <div className="dt-facc" style={{ display: 'block', fontSize: '.8rem' }}>
      <div style={{ color: 'var(--dt-text-3)', marginBottom: 2 }}>{label}</div>
      <div className="dt-mono" style={{ color: 'var(--dt-text)' }}>{value}</div>
    </div>
  );

  const wizard = selectedSession && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header card */}
      <div className="dt-card"><div className="c" style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--dt-text)' }}>{selectedSession.site.name}</span>
            {renderChip(selectedSession.status, selectedSession.status)}
          </div>
          <div style={{ fontSize: '.8rem', color: 'var(--dt-text-3)', marginTop: 3 }}>
            Site ID: <span className="dt-mono">{selectedSession.siteId}</span> · Publish is blocked until the model is ready and, if an E57 exists, alignment is verified.
          </div>
        </div>
        <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="dt-btn sm" onClick={() => refreshSelectedSession(selectedSession.id)}>Refresh</button>
          <button className="dt-btn sm" onClick={() => onOpenSiteViewer?.(selectedSession.site.id)} disabled={!onOpenSiteViewer}>Open Viewer</button>
        </span>
      </div></div>

      {/* 1. Site details */}
      <div className="dt-card"><div className="c" style={{ padding: 16 }}>
        <div className="dt-stephd" style={{ marginBottom: 12 }}>{stepCircle(1)}<h3>Site details</h3></div>
        <div className="dt-grid2" style={{ marginBottom: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="dt-flabel">Site name</label>
            <input className="dt-field" value={siteNameDraft} onChange={(e) => setSiteNameDraft(e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="dt-flabel">Description</label>
            <textarea className="dt-field" value={siteDescriptionDraft} onChange={(e) => setSiteDescriptionDraft(e.target.value)} style={{ minHeight: 90, resize: 'vertical' }} />
          </div>
        </div>
        <button className="dt-btn pri sm" onClick={handleSaveSiteDetails} disabled={savingSiteDetails}>
          {savingSiteDetails ? 'Saving...' : 'Save details'}
        </button>
      </div></div>

      {/* 2. Model source */}
      <div className="dt-card"><div className="c" style={{ padding: 16 }}>
        <div className="dt-stephd" style={{ marginBottom: 12 }}>{stepCircle(2)}<h3>Model source</h3></div>
        <div className="dt-seg" style={{ flexWrap: 'wrap' }}>
          {[
            { key: 'acc', label: 'ACC linked RVT' },
            { key: 'local_ifc', label: 'Local IFC upload' },
            { key: 'local_rvt', label: 'Local RVT upload' }
          ].map((option) => (
            <button
              key={option.key}
              className={selectedSession.modelSourceType === option.key ? 'on' : undefined}
              onClick={() => handleSetModelSource(option.key as 'acc' | 'local_ifc' | 'local_rvt')}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div></div>

      {/* 3. Link ACC model */}
      {selectedSession.modelSourceType === 'acc' && (
        <div className="dt-card"><div className="c" style={{ padding: 16 }}>
          <div className="dt-stephd" style={{ marginBottom: 12 }}>{stepCircle(3)}<h3>Link ACC model</h3></div>
          {accConnections.length === 0 ? (
            <div>
              <div style={{ color: 'var(--dt-text-2)', fontSize: '.88rem', marginBottom: 12 }}>
                This site does not have an ACC connection yet.
              </div>
              <button className="dt-btn pri sm" onClick={handleConnectACC} disabled={accLoading}>
                {accLoading ? 'Redirecting...' : 'Connect ACC'}
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
                <select className="dt-field" value={accSelectedConnection?.id || ''} onChange={(e) => handleSelectConnection(e.target.value)}>
                  <option value="">Select connection</option>
                  {accConnections.map((connection) => (
                    <option key={connection.id} value={connection.id}>{connection.acc_account_name}</option>
                  ))}
                </select>
                <select className="dt-field" value={accSelectedHub?.id || ''} onChange={(e) => handleSelectHub(e.target.value)} disabled={!accSelectedConnection}>
                  <option value="">Select hub</option>
                  {accHubs.map((hub) => (
                    <option key={hub.id} value={hub.id}>{hub.name}</option>
                  ))}
                </select>
                <select className="dt-field" value={accSelectedProject?.id || ''} onChange={(e) => handleSelectProject(e.target.value)} disabled={!accSelectedHub}>
                  <option value="">Select project</option>
                  {accProjects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>

              <div className="dt-facc" style={{ marginBottom: 12, fontSize: '.84rem' }}>
                <b style={{ color: 'var(--dt-text)' }}>Folder path</b>
                <span style={{ color: 'var(--dt-text-3)' }}>
                  {accFolderPath.length === 0 ? '/' : `/${accFolderPath.map((item) => item.name).join('/')}`}
                </span>
                <button className="dt-btn sm" style={{ marginInlineStart: 'auto' }} onClick={handleNavigateUp} disabled={accFolderPath.length === 0}>Up</button>
              </div>

              <div style={{ border: '1px solid var(--dt-border)', borderRadius: 10, overflow: 'hidden' }}>
                {accFolderContents.map((item) => (
                  <div key={item.id} className="dt-frow" style={{ padding: '11px 12px' }}>
                    <span className="dt-fbadge">{item.type === 'folder' ? 'DIR' : accService.isRevitFile(item) ? 'RVT' : 'FILE'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="dt-fname">{item.name}</div>
                      <div className="dt-fmeta">{item.type}</div>
                    </div>
                    {item.type === 'folder' ? (
                      <button className="dt-btn sm" onClick={() => handleNavigateFolder(item)}>Open</button>
                    ) : accService.isRevitFile(item) ? (
                      <button className="dt-btn pri sm" onClick={() => handleSelectAccFile(item)}>Select RVT</button>
                    ) : (
                      <span style={{ color: 'var(--dt-text-3)', fontSize: '.78rem' }}>Unsupported</span>
                    )}
                  </div>
                ))}
                {accFolderContents.length === 0 && (
                  <div style={{ padding: 12, color: 'var(--dt-text-3)', fontSize: '.84rem' }}>No folders or RVT files in this location.</div>
                )}
              </div>

              {accPendingFile && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--dt-border)', paddingTop: 14 }}>
                  <div style={{ marginBottom: 10, color: 'var(--dt-text-2)', fontSize: '.86rem' }}>
                    Selected file: <b style={{ color: 'var(--dt-text)' }}>{accPendingFile.name}</b>
                  </div>
                  <select
                    className="dt-field"
                    style={{ marginBottom: 10 }}
                    value={accSelectedVersion ?? ''}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      if (rawValue === '') {
                        setAccSelectedVersion(null);
                        setAccSelectedVersionUrn(null);
                        return;
                      }
                      const selected = accVersions.find((version) => String(version.versionNumber) === rawValue) || null;
                      setAccSelectedVersion(selected?.versionNumber ?? null);
                      setAccSelectedVersionUrn(selected?.derivativeUrn || selected?.versionUrn || null);
                    }}
                  >
                    <option value="">Always latest version</option>
                    {accVersions.map((version) => (
                      <option key={version.versionId} value={version.versionNumber}>
                        Version {version.versionNumber} - {new Date(version.lastModifiedTime).toLocaleString()}
                      </option>
                    ))}
                  </select>
                  <button className="dt-btn pri sm" onClick={handleLinkAccFile} disabled={linkingACC}>
                    {linkingACC ? 'Linking...' : 'Link RVT to this site'}
                  </button>
                </div>
              )}

              {selectedSession.accLink && (
                <div className="dt-frow" style={{ marginTop: 14 }}>
                  <span className="dt-fbadge">RVT</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dt-fname">{selectedSession.accLink.acc_file_name}</div>
                    <div className="dt-fmeta">Current ACC link · {selectedSession.accLink.sync_status}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div></div>
      )}

      {/* 3. Upload model */}
      {(selectedSession.modelSourceType === 'local_ifc' || selectedSession.modelSourceType === 'local_rvt') && (
        <div className="dt-card"><div className="c" style={{ padding: 16 }}>
          <div className="dt-stephd" style={{ marginBottom: 12 }}>{stepCircle(3)}<h3>Upload model</h3></div>
          <input
            type="file"
            accept=".ifc,.rvt"
            className="dt-field"
            onChange={(e) => setModelUploadFile(e.target.files?.[0] || null)}
          />
          <div style={{ marginTop: 12 }}>
            <button className="dt-btn pri sm" onClick={handleUploadModel} disabled={!modelUploadFile || uploadingModel}>
              {uploadingModel ? 'Uploading...' : 'Upload model'}
            </button>
          </div>
          {selectedSession.modelFiles.length > 0 && (
            <div className="dt-frow" style={{ marginTop: 12 }}>
              <span className="dt-fbadge">{selectedSession.modelSourceType === 'local_rvt' ? 'RVT' : 'IFC'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="dt-fname">{selectedSession.modelFiles[0].fileName}</div>
                <div className="dt-fmeta">Current uploaded model</div>
              </div>
            </div>
          )}
        </div></div>
      )}

      {/* 4. Optional E57 upload */}
      <div className="dt-card"><div className="c" style={{ padding: 16 }}>
        <div className="dt-stephd" style={{ marginBottom: 12 }}>{stepCircle(4)}<h3>Optional E57 upload</h3></div>
        <input
          type="file"
          accept=".e57"
          className="dt-field"
          onChange={(e) => setE57UploadFile(e.target.files?.[0] || null)}
        />
        <div style={{ marginTop: 12 }}>
          <button className="dt-btn pri sm" onClick={handleUploadE57} disabled={!e57UploadFile || uploadingE57}>
            {uploadingE57 ? 'Uploading...' : 'Upload E57'}
          </button>
        </div>
        {e57UploadProgress && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.78rem', color: 'var(--dt-text-3)', marginBottom: 6 }}>
              <span>{e57UploadProgress.message}</span>
              <span>{e57UploadProgress.progress}%</span>
            </div>
            <div className="dt-pbar"><i style={{ width: `${e57UploadProgress.progress}%` }} /></div>
            {e57UploadProgress.status === 'processing' && (
              <div style={{ marginTop: 6, fontSize: '.76rem', color: 'var(--dt-text-3)' }}>
                Extracting panoramic images from E57 file...
              </div>
            )}
          </div>
        )}
        {selectedSession.e57File && (
          <div className="dt-frow" style={{ marginTop: 12 }}>
            <span className="dt-fbadge">E57</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="dt-fname">{selectedSession.e57File.fileName}</div>
              <div className="dt-fmeta">Current E57</div>
            </div>
          </div>
        )}
      </div></div>

      {/* 5. Fix and verify alignment */}
      <div className="dt-card"><div className="c" style={{ padding: 16 }}>
        <div className="dt-stephd" style={{ marginBottom: 12 }}>{stepCircle(5)}<h3>Fix and verify alignment</h3></div>
        {!selectedSession.e57File ? (
          <div className="dt-statebox">No E57 uploaded. This step is skipped.</div>
        ) : (
          <>
            <div style={{
              border: '1px solid var(--dt-border)',
              borderRadius: 10,
              padding: 14,
              marginBottom: 12,
              background: 'var(--dt-surface-2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10
            }}>
              <div className="dt-facc" style={{ fontSize: '.82rem' }}>
                <b style={{ color: 'var(--dt-text)' }}>Status</b>
                <span className="dt-mono">{alignmentDiagnostics.status}</span>
                <b style={{ color: 'var(--dt-text)' }}>Frames</b>
                <span className="dt-mono">{alignmentDiagnostics.sourceFrame.id || 'panorama_raw'}</span>
                <span>→</span>
                <span className="dt-mono">{alignmentDiagnostics.targetFrame.id || 'rvt_model'}</span>
                <b style={{ color: 'var(--dt-text)' }}>Units</b>
                <span className="dt-mono">{alignmentDiagnostics.sourceFrame.unit || 'unknown'}</span>
                <span>→</span>
                <span className="dt-mono">{alignmentDiagnostics.targetFrame.unit || 'unknown'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                {diagCell('Transform source', alignmentDiagnostics.transformSource)}
                {diagCell('Unit scale', alignmentDiagnostics.unitScale.x.toFixed(6))}
                {diagCell('Pose source', alignmentDiagnostics.sourceFrame.poseSource || alignmentDiagnostics.sourceFrame.source || 'unknown')}
                {diagCell('E57 bounds', formatCoordinateBounds(alignmentDiagnostics.sourceFrame.bounds))}
                {diagCell('RVT bounds', formatCoordinateBounds(alignmentDiagnostics.targetFrame.bounds))}
                {diagCell('Global offset', formatCoordinatePoint(alignmentDiagnostics.viewerFrame.globalOffset || alignmentDiagnostics.targetFrame.globalOffset))}
                {diagCell('Model to viewer', alignmentDiagnostics.viewerFrame.modelToViewerTransformAvailable === undefined ? 'unknown' : String(Boolean(alignmentDiagnostics.viewerFrame.modelToViewerTransformAvailable)))}
              </div>
              {alignmentDiagnostics.notes.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, color: 'var(--dt-text-3)', fontSize: '.78rem', lineHeight: 1.4 }}>
                  {alignmentDiagnostics.notes.map((note, index) => (
                    <span key={index}>{note}</span>
                  ))}
                </div>
              )}
            </div>

            <details open={alignmentDiagnostics.hasLegacyOverride} style={{ marginBottom: 12 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--dt-text)', fontWeight: 700, fontSize: '.86rem' }}>
                Advanced legacy manual override
              </summary>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ color: 'var(--dt-text-3)', fontSize: '.8rem', lineHeight: 1.5 }}>
                  Manual offsets and scale are preserved for existing sites and take precedence when saved.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                  {(['x', 'y', 'z'] as const).map((axis) => (
                    <label key={axis} className="dt-flabel" style={{ textTransform: 'none', letterSpacing: 'normal' }}>
                      Offset {axis.toUpperCase()}
                      <input
                        type="number"
                        step="0.1"
                        className="dt-field"
                        style={{ marginTop: 6 }}
                        value={transform.translation[axis]}
                        onChange={(e) => setTransform((prev) => ({
                          ...prev,
                          translation: {
                            ...prev.translation,
                            [axis]: Number(e.target.value)
                          }
                        }))}
                      />
                    </label>
                  ))}
                  <label className="dt-flabel" style={{ textTransform: 'none', letterSpacing: 'normal' }}>
                    Rotation (deg)
                    <input
                      type="number"
                      step="0.1"
                      className="dt-field"
                      style={{ marginTop: 6 }}
                      value={transform.rotationDeg}
                      onChange={(e) => setTransform((prev) => ({ ...prev, rotationDeg: Number(e.target.value) }))}
                    />
                  </label>
                  {(['x', 'y', 'z'] as const).map((axis) => (
                    <label key={`scale-${axis}`} className="dt-flabel" style={{ textTransform: 'none', letterSpacing: 'normal' }}>
                      Scale {axis.toUpperCase()}
                      <input
                        type="number"
                        step="0.001"
                        min="0.001"
                        className="dt-field"
                        style={{ marginTop: 6 }}
                        value={transform.scale[axis]}
                        onChange={(e) => setTransform((prev) => ({
                          ...prev,
                          scale: {
                            ...prev.scale,
                            [axis]: Number(e.target.value)
                          }
                        }))}
                      />
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: 'var(--dt-text-2)', fontSize: '.85rem' }}>
                  {[
                    ['swapXY', 'Swap X/Y'],
                    ['flipX', 'Flip X'],
                    ['flipY', 'Flip Y'],
                    ['flipZ', 'Flip Z']
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={Boolean((transform as any)[key])}
                        onChange={(e) => setTransform((prev) => ({ ...prev, [key]: e.target.checked }))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div>
                  <button className="dt-btn pri sm" onClick={handleSaveAlignment} disabled={savingTransform}>
                    {savingTransform ? 'Saving...' : 'Save manual override'}
                  </button>
                </div>
              </div>
            </details>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="dt-btn sm" onClick={() => onOpenSiteViewer?.(selectedSession.site.id)}>
                Open in viewer to inspect
              </button>
              <button className="dt-btn pri sm" onClick={handleVerifyAlignment} disabled={verifyingAlignment}>
                {verifyingAlignment ? 'Verifying...' : 'Verify alignment'}
              </button>
              {selectedSession.alignmentVerifiedAt && renderChip('Verified', 'accepted')}
            </div>
          </>
        )}
      </div></div>

      {/* 6. Users */}
      <div className="dt-card"><div className="c" style={{ padding: 16 }}>
        <div className="dt-stephd" style={{ marginBottom: 12 }}>{stepCircle(6)}<h3>Users</h3></div>
        <div className="dt-grid2" style={{ marginBottom: 16, alignItems: 'start' }}>
          <div>
            <label className="dt-flabel">Add existing user</label>
            <input
              className="dt-field"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              placeholder="Search by name or email"
            />
            {searchingUsers && <div style={{ color: 'var(--dt-text-3)', fontSize: '.78rem', marginTop: 8 }}>Searching...</div>}
            <div style={{ marginTop: 8 }}>
              {userSearchResults.map((user) => (
                <div key={user.id} className="dt-grow">
                  <span className="dt-uav" style={avatarStyle}>{initials(user.name || user.email)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{user.name}</div>
                    <div style={{ fontSize: '.76rem', color: 'var(--dt-text-3)' }}>{user.email}</div>
                  </div>
                  <button className="dt-btn sm" onClick={() => handleAddExistingUser(user)} disabled={addingInvite}>
                    Add
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="dt-flabel">Invite new user</label>
            <input className="dt-field" style={{ marginBottom: 10 }} value={newInviteName} onChange={(e) => setNewInviteName(e.target.value)} placeholder="Full name" />
            <input className="dt-field" style={{ marginBottom: 10 }} value={newInviteEmail} onChange={(e) => setNewInviteEmail(e.target.value)} placeholder="Email" />
            <input className="dt-field" style={{ marginBottom: 10 }} value={newInviteOrganization} onChange={(e) => setNewInviteOrganization(e.target.value)} placeholder="Organization" />
            <button className="dt-btn pri sm" onClick={handleAddNewInvite} disabled={addingInvite}>
              {addingInvite ? 'Adding...' : 'Add recipient'}
            </button>
          </div>
        </div>

        <div>
          <label className="dt-flabel">Recipients</label>
          <div>
            {selectedSession.invites.map((invite) => (
              <div key={invite.id} className="dt-grow">
                <span className="dt-uav" style={avatarStyle}>{initials(invite.name || invite.email)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{invite.name || invite.email}</div>
                  <div style={{ fontSize: '.76rem', color: 'var(--dt-text-3)' }}>{invite.email}</div>
                </div>
                {renderChip(invite.status, invite.status)}
                <button className="dt-btn sm dt-dngr" onClick={() => handleDeleteInvite(invite.id)}>
                  Remove
                </button>
              </div>
            ))}
            {selectedSession.invites.length === 0 && (
              <div style={{ color: 'var(--dt-text-3)', fontSize: '.84rem' }}>No users added yet.</div>
            )}
          </div>
        </div>
      </div></div>

      {/* Assigned onboarding managers (admin only) */}
      {isAdmin && (
        <div className="dt-card"><div className="c" style={{ padding: 16 }}>
          <div className="dt-stephd" style={{ marginBottom: 12 }}><h3>Assigned onboarding managers</h3></div>
          <input
            className="dt-field"
            style={{ marginBottom: 10 }}
            value={managerSearchQuery}
            onChange={(e) => setManagerSearchQuery(e.target.value)}
            placeholder="Search user to assign"
          />
          <div style={{ marginBottom: 16 }}>
            {managerSearchResults.map((user) => (
              <div key={user.id} className="dt-grow">
                <span className="dt-uav" style={avatarStyle}>{initials(user.name || user.email)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{user.name}</div>
                  <div style={{ fontSize: '.76rem', color: 'var(--dt-text-3)' }}>{user.email}</div>
                </div>
                {/* Only someone who can work on the site may run its onboarding (Users & Groups
                    step 4): a platform admin, or a Data creator / Site admin there. */}
                {user.can_manage === false ? (
                  <span style={{ fontSize: '.76rem', color: 'var(--dt-text-3)' }}>
                    Needs a Data creator or Site admin role on this site
                  </span>
                ) : (
                  <button className="dt-btn sm" onClick={() => handleAssignManager(user.id)} disabled={assigningManager}>
                    Assign
                  </button>
                )}
              </div>
            ))}
          </div>
          <div>
            {selectedSession.assignments.map((manager) => (
              <div key={manager.userId} className="dt-grow">
                <span className="dt-uav" style={avatarStyle}>{initials(manager.name || manager.email)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{manager.name}</div>
                  <div style={{ fontSize: '.76rem', color: 'var(--dt-text-3)' }}>{manager.email}</div>
                </div>
                <button className="dt-btn sm dt-dngr" onClick={() => handleRemoveManager(manager.userId)}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div></div>
      )}

      {/* 7. Review and publish */}
      <div className="dt-card"><div className="c" style={{ padding: 16 }}>
        <div className="dt-stephd" style={{ marginBottom: 12 }}>{stepCircle(7)}<h3>Review and publish</h3></div>
        <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {renderChip(selectedSession.readiness.modelReady ? 'Model ready' : 'Model pending', selectedSession.readiness.modelReady ? 'ready' : 'processing', true)}
          {renderChip(selectedSession.readiness.e57Required ? 'E57 attached' : 'No E57', selectedSession.readiness.e57Required ? 'queued' : 'draft', true)}
          {renderChip(selectedSession.readiness.alignmentVerified ? 'Alignment verified' : 'Alignment pending', selectedSession.readiness.alignmentVerified ? 'accepted' : 'processing', true)}
        </div>

        {selectedSession.readiness.blockers.length > 0 ? (
          <ul style={{ color: 'var(--dt-text-2)', paddingInlineStart: 18, marginTop: 0, fontSize: '.84rem' }}>
            {selectedSession.readiness.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        ) : (
          <div style={{ color: 'var(--dt-text-2)', fontSize: '.84rem', marginBottom: 12 }}>All required checks passed. Publish will grant access and send emails.</div>
        )}

        <button
          className="dt-btn pri"
          onClick={handlePublish}
          disabled={!selectedSession.readiness.canPublish || publishing || Boolean(selectedSession.publishedAt)}
          style={(!selectedSession.readiness.canPublish || publishing || Boolean(selectedSession.publishedAt)) ? { opacity: .55, cursor: 'not-allowed' } : undefined}
        >
          {publishing ? 'Publishing...' : selectedSession.publishedAt ? 'Already published' : 'Publish project'}
        </button>

        <div style={{ marginTop: 18 }}>
          <label className="dt-flabel">Email delivery</label>
          <div>
            {selectedSession.outbox.map((item) => (
              <div key={item.id} className="dt-grow">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>{item.recipientEmail}</span>
                    {renderChip(item.status, item.status === 'failed' ? 'failed_email' : item.status)}
                  </div>
                  <div style={{ color: 'var(--dt-text-3)', fontSize: '.76rem' }}>{item.subject}</div>
                  {item.lastError && <div style={{ color: 'var(--dt-bad)', fontSize: '.76rem', marginTop: 2 }}>{item.lastError}</div>}
                </div>
                {(item.status === 'failed' || item.status === 'pending') && (
                  <button className="dt-btn sm" onClick={() => handleResendEmail(item.id)}>
                    Resend
                  </button>
                )}
              </div>
            ))}
            {selectedSession.outbox.length === 0 && (
              <div style={{ color: 'var(--dt-text-3)', fontSize: '.84rem' }}>
                Emails will appear here after publish.
              </div>
            )}
          </div>
        </div>
      </div></div>
    </div>
  );

  const rightContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {error && (
        <div style={{ border: '1px solid var(--dt-bad)', color: 'var(--dt-bad)', background: 'var(--dt-bad-t)', borderRadius: 10, padding: '12px 14px', fontSize: '.86rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ border: '1px solid var(--dt-good)', color: 'var(--dt-good)', background: 'var(--dt-good-t)', borderRadius: 10, padding: '12px 14px', fontSize: '.86rem' }}>
          {success}
        </div>
      )}
      {loading && <div className="dt-statebox">Loading onboarding projects...</div>}

      {!selectedSession && !loading && (
        isEmbedded ? (
          <div className="dt-card"><div className="c" style={{ padding: 16 }}>
            <div className="dt-stephd" style={{ marginBottom: 10 }}><h3>Guided onboarding</h3></div>
            <div style={{ color: 'var(--dt-text-2)', fontSize: '.88rem', marginBottom: 12 }}>
              This site does not have an onboarding session yet.
            </div>
            <button
              className="dt-btn pri sm"
              onClick={handleStartOnboardingForExistingSite}
              disabled={creating || !embeddedSiteId}
            >
              {creating ? 'Starting...' : 'Start onboarding for this site'}
            </button>
          </div></div>
        ) : (
          <div className="dt-statebox">Create or select an onboarding project to begin.</div>
        )
      )}

      {wizard}
    </div>
  );

  if (isEmbedded) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'}>
        {rightContent}
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="dt-body">
      <div className="dt-phead">
        <div className="pic">{IC_ONBOARD}</div>
        <div>
          <h2>{t('onboarding')}</h2>
          <div className="sub">Create sites, attach models, align E57, add users, publish.</div>
        </div>
        <div className="pa">
          <button className="dt-btn pri" onClick={() => setShowCreateForm((prev) => !prev)}>
            {showCreateForm ? 'Close' : 'New'}
          </button>
        </div>
      </div>

      <div className="dt-cols" style={{ gridTemplateColumns: '260px 1fr' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {showCreateForm && (
            <div className="dt-card"><div className="c" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                className="dt-field"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                placeholder="Site name"
              />
              <textarea
                className="dt-field"
                value={newSiteDescription}
                onChange={(e) => setNewSiteDescription(e.target.value)}
                placeholder="Description"
                style={{ minHeight: 90, resize: 'vertical' }}
              />
              <button className="dt-btn pri sm" onClick={handleCreateProject} disabled={creating}>
                {creating ? 'Creating...' : 'Create onboarding project'}
              </button>
            </div></div>
          )}

          <div className="dt-card"><div className="c" style={{ padding: '8px 8px 10px' }}>
            <div style={{ fontSize: '.72rem', color: 'var(--dt-text-3)', fontWeight: 600, padding: '4px 6px 8px' }}>
              {refreshing ? 'Refreshing...' : `${sessions.length} project${sessions.length === 1 ? '' : 's'}`}
            </div>
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`dt-stlrow${selectedSessionId === session.id ? ' sel' : ''}`}
                onClick={() => setSelectedSessionId(session.id)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{session.site.name}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--dt-text-3)', fontWeight: 500 }}>
                    Step {getSessionStep(session)} of 7 · {session.readiness.blockers.length > 0
                      ? session.readiness.blockers[0]
                      : session.publishedAt
                        ? 'Published'
                        : 'Ready to publish'}
                  </div>
                </div>
                {renderChip(session.status, session.status)}
              </div>
            ))}
          </div></div>
        </div>

        {rightContent}
      </div>
    </div>
  );
};

export default ProjectOnboardingPage;
